import { Paper, pdsTv } from '@nestle-npus-nbm/pds-ui';
import React, { forwardRef } from 'react';

export enum IMAGE_POSITION {
  TOP = 'top',
  BACKGROUND = 'background',
  BANNER = 'banner',
  BANNER_HALF = 'banner_half',
  NONE = ' none',
}

export type SponsoredAdVariant = 'dark' | 'light' | 'neutral';

export interface Props {
  imagePosition: IMAGE_POSITION;
  title: string;
  body: string;
  variant: SponsoredAdVariant;
  cta: React.ReactNode;
  image?: React.ReactNode;
  className?: string;
}

export const SponsoredAd: React.FC<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      image,
      imagePosition = IMAGE_POSITION.NONE,
      title,
      body,
      cta,
      variant = 'light',
    }: Props,
    ref,
  ) => {
    const sponsorAd = pdsTv({
      // Weird caclulation in order to not have the border bottom hidden when placed in filmstrip.
      base: ['pds-relative pds-h-[calc(100%-2px)]'],
      slots: {
        ad: [
          'pds-flex pds-h-full pds-flex-col pds-items-center pds-justify-between',
          'pds-box-border pds-gap-4 pds-p-4.5 pds-text-center',
        ],
        imageContainer:
          'pds-flex pds-min-h-[150px] pds-w-full pds-rounded-xl [&_>_*]:pds-w-full [&_>_*]:pds-object-cover',
        content: 'pds-flex pds-w-full pds-flex-col pds-items-center pds-justify-center md:pds-pb-0',
        title:
          'pds-relative pds-overflow-hidden pds-text-ellipsis pds-text-body-lg pds-font-bold md:pds-text-title-sm',
        body: 'pds-relative pds-overflow-hidden pds-text-ellipsis pds-text-body-md [overflow-wrap:anywhere]',
        actionsContainer: 'pds-flex pds-w-full pds-flex-col pds-justify-center',
      },
      variants: {
        imagePosition: {
          [IMAGE_POSITION.BACKGROUND]: {
            ad: 'pds-min-h-[390px] pds-pt-0 md:pds-h-full',
            imageContainer: 'pds-absolute pds-z-0 pds-h-full pds-rounded-xl [&_>_*]:pds-w-full',
            content: 'pds-mt-4 pds-h-full pds-justify-start',
            title: 'pds-z-20 pds-mt-4',
            body: 'pds-z-20',
            actionsContainer: 'pds-z-20 pds-mt-auto',
          },
          [IMAGE_POSITION.TOP]: {
            ad: 'pds-p-0 pds-pb-4 md:pds-h-full',
            imageContainer: 'pds-h-[150px]',
            content: 'pds-p-4',
            title: 'pds-px-4.5',
            body: 'pds-px-4.5',
            actionsContainer: 'pds-px-4.5 pds-pt-4.5',
          },
          [IMAGE_POSITION.BANNER]: {
            ad: [
              'md:pds-z-0',
              'md:pds-max-h-[300px] md:pds-min-h-[200px] md:pds-w-full md:pds-max-w-[1216px]',
              'pds-p-4',
            ],
            imageContainer:
              'pds-top-0 pds-m-[-16px] pds-w-auto md:pds-absolute md:pds-z-0 md:pds-m-0 md:pds-h-full md:pds-w-full md:[&_>*]:pds-w-full',
            content:
              'pds-flex pds-w-full pds-flex-col pds-py-4 pds-py-5 md:pds-items-start md:pds-px-4 md:pds-py-0 md:pds-text-left',
            title: 'pds-text-body-lg md:pds-text-title-sm',
            body: 'md:pds-flex-end md:pds-z-20 md:pds-w-[462px]',
            actionsContainer: 'md:pds-z-20 md:pds-px-4',
          },
          [IMAGE_POSITION.BANNER_HALF]: {
            ad: [
              'md:pds-grid md:pds-grid-cols-2 md:pds-grid-rows-2',
              'pds-p-0 pds-pb-4 md:pds-gap-4 md:pds-p-0',
              'md:pds-max-h-[288px] md:pds-min-h-[232px] md:pds-w-full md:pds-max-w-[1216px]',
              'pds-rounded pds-shadow-weak',
            ],
            imageContainer: [
              'pds-col-span-1 pds-h-[200px] pds-w-full md:pds-row-span-2',
              'md:pds-h-full',
              'md:pds-rounded-r-none',
            ],
            content:
              'md:pds-flex-column pds-col-span-1 pds-px-4 pds-py-4 md:pds-w-full md:pds-justify-center md:pds-gap-0',
            title: 'pds-text-body-lg md:pds-text-title-sm',
            body: 'pds-text-body-md',
            actionsContainer: 'pds-col-span-1 pds-row-span-1 pds-items-center pds-px-4 md:pds-p-0',
          },
          [IMAGE_POSITION.NONE]: {
            ad: 'pds-p-0 pds-py-4',
            imageContainer: 'pds-hidden',
            content: 'pds-p-5',
            actionsContainer: 'pds-px-5',
          },
        },
        adVariant: {
          dark: {
            base: 'pds-bg-neutral',
            title: 'pds-text-primary-text',
            body: 'pds-text-primary-text',
          },
          light: {
            base: 'pds-bg-surface',
            title: 'pds-text-neutral',
            body: 'pds-text-neutral',
          },
          neutral: {
            base: 'pds-bg-surface-foreground',
          },
        },
      },
      compoundVariants: [
        {
          hasLogo: false,
          imagePosition: IMAGE_POSITION.BANNER,
          class: {
            content: 'md:pds-justify-end',
          },
        },
        {
          hasLogo: false,
          imagePosition: IMAGE_POSITION.BANNER_HALF,
          class: {
            content: 'md:pds-justify-end',
          },
        },
        {
          hasLegal: false,
          imagePosition: IMAGE_POSITION.BANNER_HALF,
          class: {
            base: 'md:pds-min-h-[200px]',
          },
        },
        {
          hasLegal: false,
          imagePosition: IMAGE_POSITION.BANNER,
          class: {
            base: 'md:pds-min-h-[200px]',
          },
        },
      ],
    });
    const {
      base,
      ad,
      imageContainer,
      content,
      title: titleStyles,
      body: bodyStyles,
      actionsContainer,
    } = sponsorAd({
      imagePosition,
      adVariant: variant,
    });
    return (
      <Paper className={base({ className })}>
        <div className={ad()} ref={ref}>
          {image ? <div className={imageContainer()}>{image}</div> : null}

          <div className={content()}>
            <h2
              className={titleStyles()}
              style={{
                // @ts-ignore
                textWrap: 'balance',
              }}
            >
              {title}
            </h2>
            <div
              className={bodyStyles()}
              style={{
                // @ts-ignore
                textWrap: 'balance',
              }}
            >
              {body}
            </div>
          </div>
          {cta && <div className={actionsContainer()}>{cta}</div>}
        </div>
      </Paper>
    );
  },
);
